import { Segment, TimeRange, Timestamp } from '@mi-front/core';
import { ClickableGraphArea, SegmentsStream } from '@mi-front/ui';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { computeGapsBetweenSegments } from '../../helpers/compute-gaps-between-segments';
import { Cursor } from './cursor/Cursor';
import { Playhead } from './playhead/Playhead';
import { Selection } from './selection/Selection';

interface Props {
  readonly segments: readonly Segment[];
  readonly range: TimeRange<number>;
  readonly onTsClick: (ts: number) => void;
  readonly width: number;
  readonly height: number;
  readonly children?: React.ReactNode | ((p: { hoveredTs: Timestamp | null }) => React.ReactNode);
  readonly setHoveredTs: (ts: Timestamp | null) => void;
  readonly hoveredTs: Timestamp | null;
  readonly tz?: string;
  readonly thumbnails?: string[];
  readonly showThumbnail?: boolean;
  readonly streamRange?: {
    from: number;
    to: number;
  };
}

const FocusWrapper = styled.div<{ disabled?: boolean }>`
  position: relative;
  transition: opacity 0.2s;
  ${(p: any) =>
    p.disabled &&
    `
    opacity: 0.6;
    pointer-events: none;
  `}
`;

/**
 * Focus z-index order:
 *
 * - cursor set marker
 * - hover cursor line
 * - cursor set draggable handle
 * - hover cursor time label
 */

export function FocusP(p: Props) {
  const onBlurHandler = useCallback(() => {
    p.setHoveredTs(null);
  }, [p.setHoveredTs]);

  // Compute gaps between segments to make them un-clickable
  const unclickableRanges = useMemo(
    () => computeGapsBetweenSegments(p.segments, p.range),
    [p.segments, p.range],
  );

  return (
    <FocusWrapper data-testid="logger-focus" className="div-focus-wrapper">
      <SegmentsStream
        fadeSides
        tz={p.tz}
        height={p.height}
        width={p.width}
        range={p.range}
        segments={p.segments}
        className="div-focus-stream"
        renderHtml={() => (
          <>
            <ClickableGraphArea
              tz={p.tz}
              onTsClick={p.onTsClick}
              onHover={p.setHoveredTs}
              onBlur={onBlurHandler}
              showHoverCursor
              unclickableRanges={unclickableRanges}
            />
            {typeof p.children === 'function'
              ? p.children({
                  hoveredTs: p.hoveredTs,
                })
              : p.children}
            <Playhead />
            <Cursor />
            <Selection />
          </>
        )}
      />
    </FocusWrapper>
  );
}
